/**
 * Image Utility Functions
 * Ensures images load correctly with proper URL resolution and error handling
 */

/**
 * Converts relative image paths to absolute URLs
 * Handles both local development and production scenarios
 *
 * @param imagePath - The image path from the API (can be relative or absolute)
 * @returns Absolute URL to the image
 *
 * @example
 * getImageUrl('/uploads/image.jpg') -> 'http://localhost:3001/uploads/image.jpg'
 * getImageUrl('http://cdn.example.com/image.jpg') -> 'http://cdn.example.com/image.jpg'
 * getImageUrl(null) -> '/placeholder.png'
 */
export const getImageUrl = (imagePath: string | undefined | null): string => {
  // No image path provided
  if (!imagePath) {
    return '/placeholder.png';
  }

  // Already an absolute URL (starts with http:// or https://)
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // Relative URL - prepend API base URL
  const apiBase = import.meta.env.VITE_API_BASE_URL || ''; // Use empty string for Vite proxy

  // Ensure we don't double up on slashes
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;

  return `${apiBase}${cleanPath}`;
};

/**
 * Validates if an image URL is accessible
 * Useful for debugging image loading issues
 *
 * @param imageUrl - The image URL to validate
 * @returns Promise<boolean> - True if image is accessible, false otherwise
 */
export const validateImageUrl = async (imageUrl: string): Promise<boolean> => {
  try {
    const response = await fetch(imageUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error('Image validation failed:', imageUrl, error);
    return false;
  }
};

/**
 * Preloads an image to ensure it's cached before rendering
 * Returns a promise that resolves when image is loaded
 *
 * @param imageUrl - The image URL to preload
 * @returns Promise that resolves with image dimensions or rejects on error
 */
export const preloadImage = (imageUrl: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
};

/**
 * Gets the best available image URL from an image object
 * Prefers higher quality versions when available
 *
 * @param imageObj - Image object with multiple size options
 * @returns Best available image URL
 */
export const getBestImageUrl = (
  imageObj: {
    url?: string;
    medium_url?: string;
    large_url?: string;
    thumbnail_url?: string;
  } | undefined | null,
  preferSize: 'thumbnail' | 'medium' | 'large' | 'original' = 'medium'
): string => {
  if (!imageObj) {
    return '/placeholder.png';
  }

  // Priority order based on preferred size
  const priorityMap = {
    thumbnail: ['thumbnail_url', 'url', 'medium_url', 'large_url'],
    medium: ['medium_url', 'url', 'large_url', 'thumbnail_url'],
    large: ['large_url', 'medium_url', 'url', 'thumbnail_url'],
    original: ['url', 'large_url', 'medium_url', 'thumbnail_url']
  };

  const priorities = priorityMap[preferSize];

  for (const key of priorities) {
    const value = imageObj[key as keyof typeof imageObj];
    if (value) {
      return getImageUrl(value);
    }
  }

  return '/placeholder.png';
};

/**
 * React hook for managing image loading state
 * Can be used in components to show loading/error states
 */
export const useImageLoadState = () => {
  const [loadStates, setLoadStates] = React.useState<{
    [url: string]: 'loading' | 'loaded' | 'error';
  }>({});

  const handleImageLoad = React.useCallback((url: string) => {
    setLoadStates(prev => ({ ...prev, [url]: 'loaded' }));
  }, []);

  const handleImageError = React.useCallback((url: string) => {
    setLoadStates(prev => ({ ...prev, [url]: 'error' }));
  }, []);

  const setImageLoading = React.useCallback((url: string) => {
    setLoadStates(prev => ({ ...prev, [url]: 'loading' }));
  }, []);

  return {
    loadStates,
    handleImageLoad,
    handleImageError,
    setImageLoading,
    getLoadState: (url: string) => loadStates[url] || 'loading'
  };
};

/**
 * Image component props enhancer
 * Adds error handling and logging to image elements
 *
 * @param src - Image source URL
 * @param alt - Alt text for accessibility
 * @returns Props object for img element with error handling
 */
export const getImageProps = (
  src: string | undefined | null,
  alt: string = 'Image',
  onLoad?: () => void,
  onError?: () => void
) => {
  const imageUrl = getImageUrl(src);

  return {
    src: imageUrl,
    alt,
    onLoad: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.target as HTMLImageElement;
      console.log(
        '✅ Image loaded:',
        img.src,
        `${img.naturalWidth}x${img.naturalHeight}px`
      );
      onLoad?.();
    },
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.target as HTMLImageElement;
      console.error('❌ Image load failed:', img.src);

      // Set placeholder on error
      if (img.src !== '/placeholder.png') {
        img.src = '/placeholder.png';
      }

      onError?.();
    }
  };
};

/**
 * Checks if an image has actually loaded and rendered
 * Useful for testing and validation
 *
 * @param imgElement - The HTMLImageElement to check
 * @returns Object with load status details
 */
export const getImageLoadStatus = (imgElement: HTMLImageElement) => {
  return {
    complete: imgElement.complete,
    naturalWidth: imgElement.naturalWidth,
    naturalHeight: imgElement.naturalHeight,
    src: imgElement.src,
    currentSrc: imgElement.currentSrc,
    isLoaded: imgElement.complete && imgElement.naturalWidth > 0,
    isBroken: imgElement.complete && imgElement.naturalWidth === 0,
    displaySize: {
      width: imgElement.width,
      height: imgElement.height
    }
  };
};

/**
 * Debugging utility to log all image elements on the page
 * Helps identify which images failed to load
 */
export const debugAllImages = () => {
  const images = document.querySelectorAll('img');
  console.group('🖼️  Image Debug Report');
  console.log(`Total images on page: ${images.length}`);

  images.forEach((img, index) => {
    const status = getImageLoadStatus(img);
    const emoji = status.isLoaded ? '✅' : status.isBroken ? '❌' : '⏳';

    console.log(`${emoji} Image ${index + 1}:`, {
      src: img.src,
      alt: img.alt,
      ...status
    });
  });

  console.groupEnd();
};

// Export React if available (for useImageLoadState hook)
import React from 'react';
